import React from 'react';
import styled from '@emotion/styled';
import { Table, TableCell, TableHeaderCell, TableHeaderRow } from './Table';
import { Theme } from '@/theme';

type GridColumn<T> = {
  id?: string;
  header: React.ReactNode;
  width?: string | number;
  accessor: (row: T) => React.ReactNode;
};
export default GridColumn;

type Props<T extends { id: string }> = {
  columns: GridColumn<T>[];
  items: T[];
};

function buildGridTemplateColumns(columns: GridColumn<any>[]): string {
  return columns
    .map((col) => {
      if (!col.width) return '1fr';
      return typeof col.width === 'number' ? `${col.width}px` : col.width;
    })
    .join(' ');
}

const GridHeaderRow = styled(TableHeaderRow)`
  display: grid;
  min-width: unset;
  white-space: normal;
  //gap: '12px';
`;

const GridBodyRow = styled('div')<{ theme: Theme; altRow?: boolean }>(
  (props) => ({
    display: 'grid',
    ':nth-child(odd)': {
      background: props.theme.colors.tableRow,
    },
    ':nth-child(even)': {
      background: props.theme.colors.tableAltRow,
    },
    ...(typeof props.altRow !== 'undefined' && {
      background: !props.altRow
        ? props.theme.colors.tableRow
        : props.theme.colors.tableAltRow,
      ':nth-child(odd)': undefined,
      ':nth-child(even)': undefined,
    }),
  })
);

const GridCell = styled(TableCell)`
  white-space: normal;
  cursor: default;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

export function GridTable<T extends { id: string }>({
  columns,
  items,
}: Props<T>) {
  const gridTemplateColumns = buildGridTemplateColumns(columns);

  return (
    <Table>
      <GridHeaderRow style={{ gridTemplateColumns }}>
        {columns.map((col, index) => (
          <TableHeaderCell key={col.id ?? index}>{col.header}</TableHeaderCell>
        ))}
      </GridHeaderRow>
      {items.map((item, index) => (
        <GridBodyRow
          key={item.id}
          altRow={index % 2 !== 0}
          style={{ gridTemplateColumns }}
        >
          {columns.map((col) => (
            <GridCell key={col.id ?? index}>{col.accessor(item)}</GridCell>
          ))}
        </GridBodyRow>
      ))}
    </Table>
  );
}
